// src/pages/AutoNotePage.jsx - Editable note page with save functionality
import { Button } from "@/components/ui/button";
import { supabase } from '@/lib/supabase';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ArrowLeft, Save, StickyNote } from "lucide-react";
import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";

export default function AutoNotePage() {
    const { id } = useParams();
    const navigate = useNavigate();
    const queryClient = useQueryClient();

    const [note, setNote] = useState("");

    const { data: autoItem, isLoading } = useQuery({
        queryKey: ['auto', id],
        queryFn: async () => {
            const { data, error } = await supabase
                .from('auto')
                .select('targa, marca, modello, note')
                .eq('id', id)
                .single();
            if (error) throw error;
            return data;
        },
        enabled: !!id
    });

    // Sync local state when data loads
    useEffect(() => {
        if (autoItem) {
            setNote(autoItem.note || "");
        }
    }, [autoItem]);

    const saveMutation = useMutation({
        mutationFn: async (newNote) => {
            const { error } = await supabase
                .from('auto')
                .update({ note: newNote || null })
                .eq('id', id);
            if (error) throw error;
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['auto'] });
            queryClient.invalidateQueries({ queryKey: ['auto', id] });
            navigate('/auto');
        }
    });

    const hasChanges = autoItem && note !== (autoItem.note || "");

    const handleBack = () => navigate('/auto');

    if (isLoading) {
        return (
            <div className="p-3 md:p-8 min-h-screen">
                <div className="max-w-4xl mx-auto">
                    <div className="flex items-center justify-center py-20">
                        <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-blue-600"></div>
                    </div>
                </div>
            </div>
        );
    }

    if (!autoItem) {
        return (
            <div className="p-3 md:p-8 min-h-screen">
                <div className="max-w-4xl mx-auto">
                    <Button
                        onClick={handleBack}
                        variant="ghost"
                        className="mb-4 text-slate-600 hover:text-slate-800 -ml-2"
                        style={{ minHeight: '44px' }}
                    >
                        <ArrowLeft className="w-5 h-5 mr-2" />
                        Indietro
                    </Button>
                    <div className="text-center py-12">
                        <h2 className="text-xl font-semibold text-slate-700 mb-2">Auto non trovata</h2>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="p-3 md:p-8 min-h-screen">
            <div className="max-w-4xl mx-auto">
                {/* Back button */}
                <Button
                    onClick={handleBack}
                    variant="ghost"
                    className="mb-4 text-slate-600 hover:text-slate-800 -ml-2"
                    style={{ minHeight: '44px', touchAction: 'manipulation' }}
                >
                    <ArrowLeft className="w-5 h-5 mr-2" />
                    Indietro
                </Button>

                {/* Note card */}
                <div className="bg-white rounded-xl shadow-md border border-slate-200 overflow-hidden">
                    <div className="bg-gradient-to-r from-blue-500 to-blue-600 px-4 py-4">
                        <div className="flex items-center gap-3">
                            <StickyNote className="w-5 h-5 text-white" />
                            <div>
                                <h2 className="text-lg font-semibold text-white">Nota</h2>
                                <p className="text-blue-100 text-sm">
                                    {autoItem.targa} — {autoItem.marca} {autoItem.modello}
                                </p>
                            </div>
                        </div>
                    </div>
                    <div className="p-4">
                        <textarea
                            value={note}
                            onChange={(e) => setNote(e.target.value)}
                            placeholder="Inserisci una nota per questa auto..."
                            rows={6}
                            className="w-full px-3 py-2 border border-slate-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 resize-vertical text-base leading-relaxed"
                            style={{ minHeight: '120px' }}
                        />

                        <div className="flex items-center justify-end gap-3 mt-4 pt-4 border-t border-slate-200">
                            {saveMutation.isError && (
                                <span className="text-sm text-red-600">
                                    Errore nel salvataggio
                                </span>
                            )}
                            <Button
                                onClick={handleBack}
                                variant="outline"
                                disabled={saveMutation.isPending}
                                style={{ minHeight: '44px', touchAction: 'manipulation' }}
                            >
                                Annulla
                            </Button>
                            <Button
                                onClick={() => saveMutation.mutate(note)}
                                disabled={!hasChanges || saveMutation.isPending}
                                className="bg-gradient-to-r from-blue-500 to-blue-600 hover:from-blue-600 hover:to-blue-700"
                                style={{ minHeight: '44px', touchAction: 'manipulation' }}
                            >
                                <Save className="w-4 h-4 mr-2" />
                                {saveMutation.isPending ? "Salvando..." : "Salva Nota"}
                            </Button>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}
